import { ApiError, NotSentError, SavedNotShownError } from "./http";

/**
 * What the console may SAY after a write that did not succeed (2.740).
 *
 * A write has three outcomes, and the console states only what it knows:
 *
 * - it succeeded — the caller's own success sentence;
 * - it was REFUSED — the platform answered with its error envelope, an
 *   `ApiError` whose `code` is a machine code (anything but "UNKNOWN"). The
 *   answer is definite, and the platform's reason (or the console's
 *   explanation of its code) is shown;
 * - NO ANSWER — a network failure (not an `ApiError` at all), or a body that
 *   is not the envelope (code "UNKNOWN": an intermediary's HTML 502, say). The
 *   request may or may not have been applied. The console says the outcome is
 *   NOT KNOWN — never "failed", "could not", "refused" or "not saved", and
 *   never success — and re-reads the affected data so the screen shows the
 *   state as it stands.
 *
 * Generalises 2.717's `scheduleActionFailure`, the AI spend limit's
 * dropped-connection toast (2.702) and the project AI switch (2.709), so forty
 * surfaces share one sentence rather than forty.
 */

/** True only for an answer the platform gave in its own envelope. */
export function isDefiniteRefusal(err: unknown): err is ApiError {
  return err instanceof ApiError && err.code !== "UNKNOWN";
}

export interface WriteOutcome {
  /** True for a definite refusal; false when the platform's answer is not known. */
  known: boolean;
  sentence: string;
}

export interface WriteOutcomeOptions {
  /**
   * What was written, as a noun phrase for the not-known sentence: "the new
   * key", "this rule's change". Defaults to "the change".
   */
  what?: string;
  /**
   * The console's explanation of a definite refusal, keyed by its code. Return
   * undefined to fall back to the platform's own sentence.
   */
  refusal?: (err: ApiError) => string | undefined;
  /**
   * Replaces the not-known sentence's second half — for a write whose result
   * cannot be read back (there is no list to re-read), or whose repeat would
   * do harm.
   */
  after?: string;
}

/** The outcome of a write that threw, in words that claim only what is known. */
export function writeOutcome(err: unknown, options: WriteOutcomeOptions = {}): WriteOutcome {
  if (isDefiniteRefusal(err)) {
    return { known: true, sentence: options.refusal?.(err) ?? refusalSentence(err) };
  }
  if (err instanceof NotSentError) return { known: true, sentence: notSentSentence(err) };
  if (err instanceof SavedNotShownError) return { known: true, sentence: savedNotShownSentence(err) };
  if (wasNeverSent(err)) return { known: true, sentence: NOT_SENT };
  return { known: false, sentence: notKnownSentence(options.what, options.after) };
}

/** "The platform refused this: <its reason>." — the reason passed through as text. */
export function refusalSentence(err: ApiError): string {
  if (!err.message.trim()) return `The platform refused this (${err.code}).`;
  return `The platform refused this: ${refusalReason(err)}`;
}

/** The platform's reason as a sentence: its own words, ended with a full stop, or its code. */
export function refusalReason(err: ApiError): string {
  const reason = err.message.trim();
  if (!reason) return `(${err.code}).`;
  return /[.!?]$/.test(reason) ? reason : `${reason}.`;
}

/**
 * "<What> was saved, but the display could not be refreshed." (2.743) — the
 * platform confirmed the write; only reading its result back failed.
 */
export function savedNotShownSentence(err: SavedNotShownError): string {
  return `${err.what} was saved, but the display could not be refreshed. ${READ_AGAIN}`;
}

/**
 * "Nothing was sent: <the console's reason>." (2.741) — a check run before the
 * request, so the outcome is known and nothing changed.
 */
export function notSentSentence(err: NotSentError): string {
  const reason = err.message.trim();
  if (!reason) return "Nothing was sent, so nothing changed.";
  return `Nothing was sent: ${/[.!?]$/.test(reason) ? reason : `${reason}.`}`;
}

/**
 * A write that never left the console: one refused by its own check before
 * sending (`NotSentError`, 2.741), or one this console has no route for over
 * HTTP (`helpers.notImplemented`). It threw before any request left, so the
 * outcome IS known — nothing changed.
 */
export function wasNeverSent(err: unknown): boolean {
  if (err instanceof NotSentError) return true;
  return err instanceof Error && !(err instanceof ApiError) && /not implemented for HTTP mode/i.test(err.message);
}

export const NOT_SENT =
  "Nothing was sent: the platform serves no route for this yet, so nothing changed.";

/**
 * One step of a multi-call write (a grid save, a clone): the platform's
 * sentence for a refusal, or NO_ANSWER, and which of the two it was.
 */
export function stepFailure(err: unknown): { error: string; unknown: boolean } {
  if (isDefiniteRefusal(err)) return { error: err.message || `refused (${err.code})`, unknown: false };
  if (err instanceof NotSentError) return { error: `not sent: ${err.message}`, unknown: false };
  if (err instanceof SavedNotShownError) return { error: "saved, but the display could not be refreshed", unknown: false };
  return { error: NO_ANSWER, unknown: true };
}

/** A step with no answer, as a list item says it. */
export const NO_ANSWER = "no answer from the platform, so whether it happened is not known";

export const READ_AGAIN = "It is being read again to show it as it stands.";

/** The sentence for a write with no answer. Uses none of the failure words. */
export function notKnownSentence(what = "the change", after: string = READ_AGAIN): string {
  return `The platform did not confirm ${what}, so whether it happened is not known. ${after}`;
}
